import { PaymentElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';

import useUser from '../../../../../shared/features/user/hooks/useUser';
import useStripeSubscription from '../../hooks/useStripeSubscription';
import useStripePayment from '../../hooks/useStripePayment';
import rootStore from '../../../rootStore/RootStore';
import { readPlanParam } from '../../../../../shared/helpers/functions/navigation';
import { Plan } from '../../../../../shared/helpers/constants/plans';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';
import StripeMark from '../StripeMark';
import useTotalUsedArea from '../../../../../shared/features/user/hooks/useTotalUsedArea';
import useTotalUsedSeats from '../../../../../shared/features/user/hooks/useTotalUsedSeats';
import { validateCheckout } from '../../helpers/functions/validateCheckout';

const PaymentMethod = observer(() => {
  const { t } = useTranslation();
  const { saveCustomerTaxInfo } = useUser(rootStore);
  const { totalUsedArea } = useTotalUsedArea(rootStore);
  const { totalUsedSeats } = useTotalUsedSeats(rootStore);
  const { confirmPayment, validatePaymentInput } = useStripePayment();
  const { saveSubscription } = useStripeSubscription();
  const {
    uiStateStore,
    checkoutStore,
    userStore,
    areaUnitStore,
    subscriptionPreviewStore,
  } = rootStore;
  const { annualPlan } = uiStateStore;
  const { areaUnit } = areaUnitStore;
  const { promocode } = checkoutStore;
  const { subscriptionPreviewGenerated: isPreviewGenerated } =
    subscriptionPreviewStore;
  const [searchParams] = useSearchParams();
  const plan = readPlanParam(searchParams) as Plan;

  const checkoutValid = validateCheckout({
    plan,
    taxId: userStore.taxId,
    taxIdType: userStore.selectedCountryInfo?.taxIdType,
    annualPlan,
    areaUnit,
    totalUsedArea,
    totalUsedSeats,
    previewGenerated: isPreviewGenerated,
  });

  const handlePayClick = async () => {
    AmplitudeAnalytics.trackSubscribeClicked(plan);

    const validationError = await validatePaymentInput();

    if (validationError) {
      return;
    }

    const saveCustomerResult = await saveCustomerTaxInfo();

    if (saveCustomerResult.error) {
      return;
    }

    const saveSubscriptionParams =
      plan === Plan.annual
        ? {
            annual: {
              area: annualPlan.area,
              seats: annualPlan.additionalSeats,
              planet: annualPlan.planetImagery,
            },
          }
        : { monthly: true };
    const result = await saveSubscription({
      ...saveSubscriptionParams,
      promocode,
    });

    if (result) {
      void confirmPayment(plan, result.clientSecret);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="h4">{t('payment-method.title')}</Typography>
      <PaymentElement
        options={{
          defaultValues: {
            billingDetails: {
              email: userStore.combinedData.userData?.email ?? '',
            },
          },
        }}
      />
      <Button
        variant="contained"
        color="primary"
        disabled={!checkoutValid}
        onClick={handlePayClick}
      >
        {plan === Plan.annual
          ? t('payment-method.pay-subscribe')
          : t('payment-method.subscribe')}
      </Button>
      <StripeMark />
    </Box>
  );
});

export default PaymentMethod;
